import { Show, createSignal } from "solid-js";
import { useApi } from "../../api/provider.tsx";
import { Async } from "../../components/Async.tsx";
import { Actions, Button, Form, TextField } from "../../components/Form.tsx";
import { Panel } from "../../components/Panel.tsx";
import { Table } from "../../components/Table.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { createLoader } from "../../lib/async.ts";
import type { Author } from "../../api/domain.ts";
import styles from "./admin.module.scss";

const blankAuthor = (): Author => ({ id: "", name: "", email: null, comment: null });

/**
 * The people behind the answers. Every questionnaire is addressed to one, and
 * every answer is attributed to one, so this is the first thing to fill in.
 */
export const AuthorsPage = () => {
  const api = useApi();
  const i18n = useI18n();

  const authors = createLoader(() => api.list("authors"));
  const [draft, setDraft] = createSignal<Author | undefined>(undefined);

  const save = async (author: Author) => {
    const result = await api.create("authors", author);
    if (!result.ok) return;
    setDraft(undefined);
    authors.reload();
  };

  return (
    <>
      <div class={styles.heading}>
        <h1>{i18n.t().authors.title}</h1>
      </div>

      <Panel
        actions={
          <Button
            label={i18n.t().authors.newAuthor}
            onClick={() => setDraft(blankAuthor())}
          />
        }
      >
        <Show when={draft()}>
          {(author) => (
            <Form>
              <TextField
                label={i18n.t().common.name}
                value={author().name}
                onInput={(name) => setDraft({ ...author(), name })}
              />
              <TextField
                label={i18n.t().authors.email}
                optional
                value={author().email ?? ""}
                onInput={(email) => setDraft({ ...author(), email: email === "" ? null : email })}
              />
              <TextField
                label={i18n.t().authors.comment}
                optional
                multiline
                value={author().comment ?? ""}
                onInput={(comment) =>
                  setDraft({ ...author(), comment: comment === "" ? null : comment })
                }
              />
              <Actions>
                <Button
                  label={i18n.t().common.create}
                  tone="primary"
                  disabled={author().name.trim() === ""}
                  onClick={() => void save(author())}
                />
                <Button label={i18n.t().common.cancel} onClick={() => setDraft(undefined)} />
              </Actions>
            </Form>
          )}
        </Show>

        <Async value={authors.value()} onRetry={authors.reload}>
          {(rows) => (
            <Table
              rows={rows}
              rowKey={(author) => author.id}
              emptyLabel={i18n.t().common.empty}
              columns={[
                { key: "name", header: i18n.t().common.name, cell: (author) => author.name },
                { key: "id", header: i18n.t().common.identity, cell: (author) => author.id },
                {
                  key: "email",
                  header: i18n.t().authors.email,
                  cell: (author) => author.email ?? "",
                },
                {
                  key: "comment",
                  header: i18n.t().authors.comment,
                  cell: (author) => author.comment ?? "",
                },
              ]}
            />
          )}
        </Async>
      </Panel>
    </>
  );
};
